import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useApp } from "../context/AppContext";
import { ArrowRight, MapPin, Phone, Mail } from "lucide-react";

export default function Home() {
  const { API, content } = useApp();
  const [featured, setFeatured] = useState([]);
  useEffect(() => { axios.get(`${API}/products`).then(r => setFeatured(r.data.slice(0,6))); }, [API]);

  const heroImg = content?.hero_image || "/condor-eagle-seal.png";
  const heroEyebrow = content?.hero_eyebrow || "EST. 2015 · NATIVE AMERICAN OWNED";
  const titleL1 = content?.hero_title_l1 || "CONDOR & EAGLE";
  const titleL2 = content?.hero_title_l2 || "TRADING POST";
  const titleAccent = content?.hero_title_accent || "AUTHENTIC. HANDMADE. NATIVE.";
  const heroPara = content?.hero_paragraph || "Owned and operated by Native Americans since 2015. Authentic crafts, jewelry, beadwork, pottery, and leatherwork — sourced and made by Native artisans across Indian Country.";
  const ctaPrimary = content?.hero_cta_primary || "BROWSE INVENTORY";
  const ctaSecondary = content?.hero_cta_secondary || "CREATE ACCOUNT";
  const businessName = content?.business_name || "Condor & Eagle Trading Post";
  const contactEmail = content?.contact_email || "info@lecroystore.com";
  const contactPhone = content?.contact_phone || "";
  const contactAddress = content?.contact_address || "";
  const footerText = content?.footer_text || "Owned & operated by Native Americans since 2015. Wado.";

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="border-b-2 border-[var(--ink)] relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 py-12 md:py-20">
          <div className="relative mx-auto mb-10 md:mb-14 max-w-2xl">
            <div className="aspect-square border-2 border-[var(--ink)] bg-[var(--paper)] overflow-hidden relative">
              <img src={heroImg} alt={businessName} className="w-full h-full object-contain p-4 bg-[var(--paper)]" data-testid="hero-img"/>
              <div className="absolute bottom-3 left-3 right-3 stripe-tape h-3"/>
            </div>
            <div className="absolute -bottom-3 -right-3 bg-[var(--accent-2)] border-2 border-[var(--ink)] px-4 py-2 font-display text-base md:text-lg rotate-3 text-[var(--ink)]">EST. 2015</div>
          </div>

          <div className="text-center md:text-left">
            <div className="font-mono text-xs tracking-widest mb-4 text-[var(--accent)]" data-testid="hero-eyebrow">{heroEyebrow}</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] mb-6" data-testid="hero-title">
              {titleL1}<br/>{titleL2}<br/><span className="text-[var(--accent)]">{titleAccent}</span>
            </h1>
            <p className="text-base md:text-lg text-[var(--ink-2)] mb-8 max-w-2xl mx-auto md:mx-0" data-testid="hero-paragraph">{heroPara}</p>
            <div className="flex flex-wrap gap-4 justify-center md:justify-start">
              <Link to="/shop" className="btn-primary inline-flex items-center gap-2" data-testid="shop-cta">{ctaPrimary} <ArrowRight size={16}/></Link>
              <Link to="/about" className="btn-outline inline-flex items-center gap-2" data-testid="about-cta">ABOUT US</Link>
              <Link to="/signup" className="btn-outline" data-testid="signup-cta">{ctaSecondary}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Info strip — only shows fields that have values */}
      {(contactAddress || contactPhone || contactEmail) && (
        <section className="bg-[var(--ink)] text-[var(--paper)] border-b-2 border-[var(--ink)]">
          <div className="max-w-7xl mx-auto px-6 py-6 grid md:grid-cols-3 gap-6 font-mono text-sm">
            {contactAddress && <div className="flex items-center gap-3" data-testid="info-address"><MapPin size={18} className="text-[var(--accent-2)]"/><span>{contactAddress}</span></div>}
            {contactPhone && <div className="flex items-center gap-3" data-testid="info-phone"><Phone size={18} className="text-[var(--accent-2)]"/><span>{contactPhone}</span></div>}
            {contactEmail && <div className="flex items-center gap-3" data-testid="info-email"><Mail size={18} className="text-[var(--accent-2)]"/><a href={`mailto:${contactEmail}`} className="break-all hover:text-[var(--accent-2)]">{contactEmail}</a></div>}
          </div>
        </section>
      )}

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-10 border-b-2 border-[var(--ink)] pb-4">
          <h2 className="font-display text-3xl md:text-5xl">FRESH ARRIVALS</h2>
          <Link to="/shop" className="font-mono text-sm underline">SEE ALL →</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-products">
          {featured.map(p => (
            <Link to={`/product/${p.product_id}`} key={p.product_id} className="card-industrial block relative" data-testid={`product-card-${p.product_id}`}>
              {p.original_price && p.original_price > p.price && (
                <div className="absolute top-3 right-3 bg-[var(--accent)] text-white font-display text-xs px-2 py-1 z-10 border-2 border-[var(--ink)]">SALE</div>
              )}
              <div className="aspect-[4/3] overflow-hidden bg-[var(--muted)] border-b-2 border-[var(--ink)]">
                <img src={p.images?.[0]} className="w-full h-full object-cover" alt={p.name}/>
              </div>
              <div className="p-4">
                <div className="font-mono text-xs text-[var(--ink-2)] mb-1">{p.sku} · {p.category}</div>
                <div className="font-display text-lg leading-tight mb-2">{p.name}</div>
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-2">
                    {p.original_price && p.original_price > p.price && <span className="font-mono text-sm line-through text-[var(--ink-2)]">${p.original_price.toFixed(2)}</span>}
                    <div className="font-display text-2xl">${p.price.toFixed(2)}</div>
                  </div>
                  <div className="font-mono text-xs">{p.stock} in stock</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <footer className="bg-[var(--teal)] text-[var(--paper)] py-10 px-6 font-mono text-sm">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
          <div>
            <div className="font-display text-2xl mb-2">{businessName.toUpperCase()}</div>
            <div className="text-xs opacity-80">{footerText}</div>
          </div>
          {(contactAddress) && (
            <div>
              <div className="text-[var(--accent-2)] text-xs tracking-widest mb-2">VISIT US</div>
              <div className="whitespace-pre-line">{contactAddress}</div>
            </div>
          )}
          <div>
            <div className="text-[var(--accent-2)] text-xs tracking-widest mb-2">CONTACT</div>
            {contactPhone && <div><a href={`tel:${contactPhone.replace(/\D/g,'')}`} className="hover:text-[var(--accent-2)]">{contactPhone}</a></div>}
            {contactEmail && <div><a href={`mailto:${contactEmail}`} className="hover:text-[var(--accent-2)] break-all">{contactEmail}</a></div>}
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-4 border-t border-white/20 text-xs opacity-70 text-center">
          © {new Date().getFullYear()} {businessName} · All rights reserved
        </div>
      </footer>
    </div>
  );
}
